/**
 * B15 (Nationality) baseCode 라벨 공유 캐시.
 *   L_BASECODE 의 B15 마스터는 COUNTRY_CODE='CM' (common) 으로 저장됨.
 *   register detail dialog 등 panel 외 위치에서도 동기 lookup 가능하도록 module-level 캐시.
 *
 * 사용 흐름:
 *   1) App 부팅 / panel 마운트 시 `prefetchB15Cache()` 호출해 캐시 채움.
 *   2) `lookupB15Label(code)` 는 동기 — 캐시 미스 시 `code` 그대로 반환.
 *   3) 캐시는 module-level singleton — 페이지 이동 시에도 유지.
 */

const cache = new Map<string, string>();
let loadingPromise: Promise<void> | null = null;
let loaded = false;

export function isB15CacheLoaded(): boolean {
  return loaded;
}

/** 동기 lookup — 캐시 hit 시 라벨, miss 시 입력 그대로 반환. */
export function lookupB15Label(code: string | null | undefined): string {
  const s = String(code || '').trim();
  if (!s) return '';
  return cache.get(s.toUpperCase()) || s;
}

/**
 * 역방향 lookup — 라벨 (예: 'MALAYSIA', 'Malaysia') → B15 baseCode (예: 'B15135').
 *   대소문자/공백 무시. 못 찾으면 빈 문자열.
 */
export function lookupB15CodeByName(name: string | null | undefined): string {
  const target = String(name || '').trim().toUpperCase().replace(/\s+/g, ' ');
  if (!target) return '';
  for (const [code, label] of cache) {
    if (String(label).trim().toUpperCase().replace(/\s+/g, ' ') === target) {
      return code;
    }
  }
  return '';
}

/** 비동기 prefetch — 중복 호출 안전 (한 번 로드되면 같은 promise 재사용). */
export async function prefetchB15Cache(): Promise<void> {
  if (loaded) return;
  if (loadingPromise) return loadingPromise;
  loadingPromise = (async () => {
    try {
      const { baseCodeApi } = await import('@/shared/api/baseCodeApi');
      const res: any = await baseCodeApi.getCodesByMainCode('CM', 'B15');
      const items: any[] = res?.data?.codes || res?.codes || res?.data || [];
      for (const it of items) {
        const code = String(it.totalCode || it.TOTAL_CODE || it.subCode || it.SUB_CODE || '').trim().toUpperCase();
        const name = String(it.codeName || it.CODE_NAME || '').trim();
        if (code && name) cache.set(code, name);
      }
      loaded = true;
    } catch (err) {
      console.warn('[B15 cache] prefetch failed:', err);
    } finally {
      loadingPromise = null;
    }
  })();
  return loadingPromise;
}
